import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { apiLogger } from "../../Utils/logger";
import { TribePostApi } from "../../routes/Tribe/post";

const nextFromMeta = (meta) => {
    const cur = meta?.current_page;
    const last = meta?.last_page;
    if (typeof cur !== 'number' || typeof last !== 'number') return undefined;
    return cur < last ? cur + 1 : undefined;
};

export const useUpdateTribePost = () => {
  return useMutation({
    mutationFn: async ({ formData, postId }) => {
      try {
        apiLogger.apiRequest("PUT", `/user/update-post/${postId}`, formData);
        const response = await TribePostApi.updatePost(formData, postId);
        apiLogger.apiResponse(
          "PUT",
          `/user/update-post/${postId}`,
          response.data,
          200
        );
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "PUT",
          `/user/update-post/${postId}`,
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useGetTribeGroups = (
  searchQuery = "",
  showGroup = "All",
  enabled = true
) => {
  return useQuery({
    queryKey: ["tribeGroups", searchQuery, showGroup],
    queryFn: async () => {
      try {
        const params = {
          search: searchQuery,
          show_group: showGroup,
          per_page: 100, // Get more results for selection
        };

        apiLogger.apiRequest("GET", "/user/tribe-group", params);
        const response = await TribePostApi.getTribeGroups(params);
        apiLogger.apiResponse("GET", "/user/tribe-group", response.data, 200);

        return response.data?.groups || [];
      } catch (error) {
        apiLogger.apiError(
          "GET",
          "/user/tribe-group",
          error,
          error.response?.status
        );
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled, // Always enabled
  });
};

export const useCreateMultiGroupPost = () => {
  return useMutation({
    mutationFn: async (formData) => {
      try {
        apiLogger.apiRequest("POST", "/user/create-multi-group-post", formData);
        const response = await TribePostApi.createMultiGroupPost(formData);
        apiLogger.apiResponse(
          "POST",
          "/user/create-multi-group-post",
          response.data,
          200
        );
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          "/user/create-multi-group-post",
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useToggleLikePost = () => {
  return useMutation({
    mutationFn: async (postId) => {
      try {
        apiLogger.apiRequest("POST", "/user/post/like", { id: postId });
        const response = await TribePostApi.likePost(postId);
        apiLogger.apiResponse("POST", "/user/post/like", response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          "/user/post/like",
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useToggleSavePost = () => {
  return useMutation({
    mutationFn: async (postId) => {
      try {
        apiLogger.apiRequest("POST", "/user/post/save", { id: postId });
        const response = await TribePostApi.toggleSavePost(postId);
        apiLogger.apiResponse("POST", "/user/post/save", response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          "/user/post/save",
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useGetPostComments = () => {
  return useMutation({
    mutationFn: async ({ postId, page = 1, per_page = 10 }) => {
      try {
        apiLogger.apiRequest("GET", "/user/post/comments", {
          post_id: postId,
          page,
          per_page,
        });
        const response = await TribePostApi.getPostComments(postId, {
          page,
          per_page,
        });
        apiLogger.apiResponse("GET", "/user/post/comments", response.data, 200);
        return response.data?.data || {};
      } catch (error) {
        apiLogger.apiError(
          "GET",
          "/user/post/comments",
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useGetPostDetails = (postId, enabled = true) => {
  return useQuery({
    queryKey: ["tribePostDetails", postId],
    queryFn: async () => {
      try {

        apiLogger.apiRequest("GET", `/user/post-details/${postId}`);
        const response = await TribePostApi.getPostDetails(postId);
        apiLogger.apiResponse(
          "GET",
          `/user/post-details/${postId}`,
          response.data,
          200
        );
        return response.data?.post || {};
      } catch (error) {
        apiLogger.apiError(
          "GET",
          `/user/post-details/${postId}`,
          error,
          error.response?.status
        );
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    enabled: enabled,
  });
};

export const useTogglePinPost = () => {
  return useMutation({
    mutationFn: async (data) => {
      try {
        apiLogger.apiRequest("POST", "/user/pin-post", data);
        const response = await TribePostApi.togglePinPost(data);
        apiLogger.apiResponse("POST", "/user/pin-post", response.data, 200);
        return response.data;
      } catch (error) {
        apiLogger.apiError(
          "POST",
          "/user/pin-post",
          error,
          error.response?.status
        );
        throw error;
      }
    },
  });
};

export const useBatchTrackViews = () => {
    return useMutation({
        mutationFn: async (payload) => {
            try {
                apiLogger.apiRequest('POST', '/user/v3/views/batch', payload);
                const response = await TribePostApi.batchTrackViews(payload.views);
                apiLogger.apiResponse('POST', '/user/v3/views/batch', response.data, 200);
                return response.data;
            } catch (error) {
                apiLogger.apiError('POST', '/user/v3/views/batch', error, error.response?.status);
                throw error;
            }
        }
    });
}

// Reactions hooks
export const useGetReactions = () => {
    return useQuery({
        queryKey: ['reactions'],
        queryFn: async () => {
            try {
                apiLogger.apiRequest('GET', '/user/reactions');
                const response = await TribePostApi.getReactions();
                apiLogger.apiResponse('GET', '/user/reactions', response.data, 200);
                return response.data?.reactions || [];
            } catch (error) {
                apiLogger.apiError('GET', '/user/reactions', error, error.response?.status);
                throw error;
            }
        },
        staleTime: 30 * 60 * 1000, // 30 minutes - reactions don't change often
        cacheTime: 60 * 60 * 1000, // 1 hour
    });
}

export const useReactToPost = () => {
    return useMutation({
        mutationFn: async ({ postId, reactionId }) => {
            try {
                const payload = reactionId ? { id: postId, reaction_id: reactionId } : { id: postId };
                apiLogger.apiRequest('POST', '/user/post/like', payload);
                const response = await TribePostApi.reactToPost(payload);
                apiLogger.apiResponse('POST', '/user/post/like', response.data, 200);
                // Return the full response so ReactionButton can access response.data.data
                return response;
            } catch (error) {
                apiLogger.apiError('POST', '/user/post/like', error, error.response?.status);
                throw error;
            }
        }
    });
}

export const useReactToComment = () => {
    return useMutation({
        mutationFn: async ({ commentId, reactionId }) => {
            try {
                const payload = reactionId ? { comment_id: commentId, reaction_id: reactionId } : { comment_id: commentId };
                apiLogger.apiRequest('POST', '/user/comment/like', payload);
                const response = await TribePostApi.reactToComment(payload);
                apiLogger.apiResponse('POST', '/user/comment/like', response.data, 200);
                // Return the full response so ReactionButton can access response.data.data
                return response;
            } catch (error) {
                apiLogger.apiError('POST', '/user/comment/like', error, error.response?.status);
                throw error;
            }
        }
    });
}

export const useGetUserPosts = (userId, enabled = true) => {
    return useInfiniteQuery({
        queryKey: ['userPosts', userId],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            try {
                apiLogger.apiRequest('GET', '/v3/posts/user-posts', {
                    page: pageParam,
                });

                const response = await TribePostApi.getUserPosts({
                    page: pageParam,
                    user_id: userId,
                });
                apiLogger.apiResponse('GET', '/v3/posts/user-posts', response.data, 200);
                const data = response.data?.data || response.data || {};
                return { posts: data.posts || [], meta: data.meta || {} };
            } catch (error) {
                apiLogger.apiError('GET', '/v3/posts/user-posts', error, error.response?.status);
                throw error;
            }
        },
        getNextPageParam: (lastPage) => nextFromMeta(lastPage?.meta),
        enabled: enabled && !!userId,
    });
}


export const useGetScheduledPosts = ({ groupId, enabled = true, perPage = 10 } = {}) => {
    return useInfiniteQuery({
        queryKey: ['scheduledPosts', groupId ?? null],
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            try {
                const params = {
                    page: pageParam,
                    per_page: perPage,
                    ...(groupId ? { group_id: groupId } : {}),
                };
                apiLogger.apiRequest('GET', '/user/tribe/schedule', params);
                const response = await TribePostApi.getScheduledPosts(params);
                apiLogger.apiResponse('GET', '/user/tribe/schedule', response.data, 200);
                const data = response.data?.data || response.data || {};
                return { posts: data.posts || [], meta: data.meta || {} };
            } catch (error) {
                apiLogger.apiError('GET', '/user/tribe/schedule', error, error.response?.status);
                throw error;
            }
        },
        getNextPageParam: (lastPage) => nextFromMeta(lastPage?.meta),
        enabled: enabled,
    });
}